/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable react-hooks/rules-of-hooks */
import classNames from 'classnames';
import React, { useRef, useState } from 'react';
import { Container } from 'src/components/layouts/Container/Container';
import HeadlineWithButtons from 'src/components/ui/HeadlineWithButtons/HeadlineWithButtons';
import { useTranslation } from 'react-i18next';
import Stepper from 'src/components/ui/Stepper/Stepper';
import WarningBox, { WarningBoxColorType } from 'src/components/ui/WarningBox/WarningBox';
import Button, { ButtonPositionProps, ButtonToneProps } from 'src/components/ui/Button/Button';
import Icon from 'src/components/ui/Icon/Icon';
import { getTermsByType } from 'src/services/terms';
import Divider from 'src/components/ui/Divider/Divider';
import { useNavigate } from 'react-router-dom';
import Chat from 'src/components/layouts/Chat/Chat';
import { InboundStatusBadge } from 'src/components/tables/InboundTable/InboundTable';
import ProgressIndicator from 'src/components/ui/ProgressIndicator/ProgressIndicator';
import { useNavigationItems } from 'src/hooks/useNavigationItems';
import { toast } from 'react-toastify';
import { ExtraUnitsBadge } from './ExtraUnitsBadge';
import styles from '../styles/Inventory.module.scss';
import { ActionBar, InboundConfiguratorForm, Section } from './InboundConfiguratorForm';
import { InboundOrder, useInboundOrderQuery } from '../../services/inbound';

interface InboundCreateShellProps {
  step?: number;
  title: string;
  headline: string;
  children: React.ReactNode;
  orderId?: number;
  status?: InboundOrder['inbound_state'];
  headerActions?: React.ReactNode;
}

export const InboundCreateShell: React.FC<InboundCreateShellProps> = ({
  step,
  children,
  headline,
  title,
  orderId,
  status,
  headerActions,
}) => {
  const { t } = useTranslation();

  const shouldFetchOrder = orderId && orderId !== 0;
  const data = shouldFetchOrder
    ? useInboundOrderQuery(
        {
          path: { inbound_order_id: orderId },
          query: { offset: 0, limit: 0 },
        },
        { enabled: true },
      )
    : { data: null, isLoading: false };
  const inboundOrder: InboundOrder | null = shouldFetchOrder ? data.data ?? null : null;

  const allUnits = inboundOrder?.all_units ?? 0;
  const doneUnits = inboundOrder?.done_units ?? 0;

  const { findAccessRights } = useNavigationItems();
  const chatAccess = findAccessRights('chatter');

  return (
    <Container buttons={[]} className={classNames(styles.root)} title={title}>
      <div className="flex items-start justify-between gap-x-12">
        <HeadlineWithButtons headline={headline} buttons={[]} />
        {headerActions}
      </div>
      {Number.isInteger(step) && (
        <div className="mt-40 mb-40 grid gap-y-24 px-32">
          <Stepper
            currentStep={step!}
            totalSteps={3}
            stepLabels={[
              t('inbound.data_step_label'),
              t('inbound.summary_step_label'),
              t('inbound.confirm_step_label'),
            ]}
          />
        </div>
      )}

      <div className="flex flex-col md:flex-row gap-20">
        <div className={`${!Number.isInteger(step) && orderId ? 'w-[calc(66.67%-1.25rem)]' : 'w-full'}`}>
          {children}
        </div>
        {!Number.isInteger(step) && orderId && (
          <div className="w-[33.33%] surface-elevated border-subtle rounded-base p-24 h-fit flex flex-col gap-y-16">
            <div className="typo-text-16--bold text-full">{t('general.progress')}</div>
            <div className="grid grid-cols-[max-content_1fr] gap-y-16 gap-x-24 items-baseline">
              {status && (
                <>
                  <span className="typo-text-12--bold whitespace-nowrap text-faded">
                    Status
                    <div className="typo-text-12--bold whitespace-nowrap text-faded opacity-0 h-0 overflow-hidden invisible">
                      {/* This little hack makes the column the same width as the first column in the chat interface below */}
                      {t('chat.add_attachment')}
                    </div>
                  </span>
                  <InboundStatusBadge status={status} />
                </>
              )}
              <span className="typo-text-12--bold whitespace-nowrap text-faded">{t('inbound.package_count')}</span>
              <ProgressIndicator
                percentage={allUnits > 0 ? (doneUnits / allUnits) * 100 : 0}
                sum={allUnits}
                progress={doneUnits}
                type="brand"
                size="large"
              />
              {(inboundOrder?.extra_units ?? 0) > 0 && (
                <>
                  <span className="typo-text-12--bold whitespace-nowrap text-faded">{t('inbound.amount_units')}</span>
                  <ExtraUnitsBadge extra={inboundOrder?.extra_units} />
                </>
              )}
            </div>
            {(chatAccess?.read || chatAccess?.read_write) && (
              <>
                <Divider className="my-24" />
                <Chat
                  withCreateEditMessage={chatAccess?.read_write}
                  model="purchase.order"
                  res_id={orderId ?? 0}
                  condensed
                />
              </>
            )}
          </div>
        )}
      </div>
    </Container>
  );
};

export const DownloadSection: React.FC<{
  title: string;
  copy: string;
  onDownloadClick: () => void;
  loading?: boolean;
}> = ({ title, copy, onDownloadClick, loading }) => {
  const { t } = useTranslation();

  return (
    <Section>
      <div className="grid grid-cols-[min-content,1fr,auto] gap-16 items-center">
        <div className="rounded-base border-subtle p-8">
          <Icon name="file" />
        </div>
        <div className="grid grid-rows-[auto,auto] gap-y-6 col-span-2 md:col-span-1">
          <span className="typo-text-14--bold">{title}</span>
          <span className="typo-text-12 text-faded">{copy}</span>
        </div>
        <Button
          disabled={loading}
          title={loading ? t('inbound.download_loading') : t('inbound.download')}
          className="col-span-full md:col-span-1"
          tone={ButtonToneProps.PRIMARY_BLUE}
          icon="download"
          iconPosition={ButtonPositionProps.AFTER}
          onClick={onDownloadClick}
        />
      </div>
    </Section>
  );
};

function InboundCreateData() {
  const { t } = useTranslation();

  const formRef = useRef<{ triggerSubmit: () => void }>(null);

  const submit = () => {
    if (formRef.current) {
      formRef.current.triggerSubmit();
    }
  };

  const navigate = useNavigate();

  const [isValid, setIsValid] = useState(false);

  return (
    <InboundCreateShell
      step={1}
      headline={t('inbound.create_headline')}
      title={`${t('inbound.inbound')} / ${t('inbound.create_cta')}`}>
      <div className="flex flex-col gap-y-20">
        <WarningBox
          colorType={WarningBoxColorType.Warning}
          title={t('inbound.warning_title')}
          icon="warning"
          copy={t('inbound.warning_copy')}
        />
        <DownloadSection
          title={t('inbound.guidelines_title')}
          copy={t('inbound.download_pdf')}
          onDownloadClick={async () => {
            const fileName = 'inbound_guidelines.pdf';
            const fileContent = await getTermsByType('inbound_guidelines');
            if (!fileContent) {
              toast.error(t('terms.downloading_error'), { autoClose: 5000 });
              return;
            }
            const url = window.URL.createObjectURL(fileContent);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
          }}
        />
        <Section className="flex flex-col gap-y-32">
          <InboundConfiguratorForm
            ref={formRef}
            onValidityChange={setIsValid}
            onSubmit={() => {
              navigate('/warehouse/inbound/create/summary');
            }}
          />
          <Divider className="" />
          <div className="flex items-start justify-center gap-4 text-center typo-text-14 status-warning-full">
            <Icon name="warning" width={16} height={16} className="status-warning-full shrink-0 mt-[1px]" />
            <span>{t('inbound.planned_date_tooltip')}</span>
          </div>
          <ActionBar>
            <Button
              title={t('inbound.cancel')}
              onClick={() => {
                navigate('/warehouse/inbound');
              }}
              tone={ButtonToneProps.SECONDARY}
            />
            <Button
              title={t('inbound.continue')}
              disabled={!isValid}
              onClick={submit}
              tone={ButtonToneProps.PRIMARY_BLUE}
            />
          </ActionBar>
        </Section>
      </div>
    </InboundCreateShell>
  );
}

export default InboundCreateData;
